(ns ui.editor2.composition
  (:require [ui.editor2.common :as common]
            [ui.editor2.defaults :as defaults]
            [ui.editor2.extension-points :as extension-points]
            ["moveable" :as Moveable]
            ["selecto" :as Selecto]
            [datascript.core :as ds]
            [clojure.string :as str]
            [replicant.string :as rstring]
            [ui.editor2.design :as design]
            ))

(defn rad->deg [radians]
  (/ (* radians 180) js/Math.PI))

(defonce moveable-instance
         (atom nil))

(defonce selecto-instance
         (atom nil))

(def editor-container-id
  "editor-container")

(defn transform-str
  [transform]
  (str
    (when-let [translate (:translate transform)]
      (str "translate("
           (str/join ", "
                     (map (fn [v]
                            (str v
                                 "px"))
                          ;; Only taking x and y, otherwise transform3d must be
                          ;; used:
                          (take 2
                                translate)))
           ")"))
    " "
    (when-let [scale (:scale transform)]
      (str "scale("
           (str/join ", "
                     (take 2 scale))
           ")"))
    (when-let [rotate (:rotate transform)]
      (str " rotate("
           rotate
           "deg)"))))

(defn get-composition-zoom
  [state]
  (get-in state
          [:editor2
           :composition-zoom]))

(defonce local-state
  (atom nil))

(defn overlay-mask
  [state content]
  [:div
   {:style {:position "absolute"
            :top 0
            :left 0
            :width "100%"
            :height "100%"
            :z-index 10}}
   [:div#overlay-mask
    {:style
     (let [space "minmax(400px, 1fr)"]
       {:display "grid"
        :grid-template-columns (str space
                                    " "
                                    (/ (:design/width defaults/defaults)
                                       (get-composition-zoom state))
                                    "px "
                                    space)
        :grid-template-rows (str space
                                 " "
                                 (/ (:design/height defaults/defaults)
                                    (get-composition-zoom state))
                                 "px "
                                 space)
        :width "100%"
        :height "100%"
        })}
    (let [cell [:div
                {:style {:background "rgba(0, 0, 0, 0.7)"
                         :z-index "1000"
                         :pointer-events "none"}}
                ]]
      (concat
        (repeat 4 cell)
        [content]
        (repeat 4 cell)))]])

(def moveable-options-select
  ;; `dragArea: true` makes Moveable render a `.moveable-area` overlay
  ;; on the controlBox (z-index 3000). Without it, Moveable's
  ;; targetGesto binds directly to the shadow target div in
  ;; `#movable-container` — but those divs are now `pointer-events:
  ;; none` (so shift-click can reach the design clip underneath for
  ;; multi-select), which means clicks on a selected clip never reach
  ;; Moveable. With `dragArea: true`, the gesture goes through the
  ;; controlBox and the shadow target only matters for size/transform
  ;; readout. (Group mode auto-enables this; we have to opt in for
  ;; single-select / individualGroupable mode.)
  (clj->js {:draggable true
            :dragArea true
            :scalable true
            :keepRatio true
            :hideDefaultLines false
            ;; Only the corner handles: the clip is scaled uniformly
            ;; (`keepRatio`), an edge handle would promise a stretch in
            ;; one direction that a rasterized sticker, an image or a
            ;; video does not do. Text clips get the e/w handles, which
            ;; really resize the text width (see below).
            :renderDirections ["nw" "ne" "se" "sw"]
            :rotatable true}))

(def moveable-options-select-text
  (clj->js {:draggable true
            :dragArea true
            :triggerAblesSimultaneously true
            :scalable {:edge []
                       :renderDirections ["nw" "ne" "se" "sw"]
                       :keepRatio true}
            :resizable {:edge ["e" "w"]
                        :renderDirections ["e" "w"]
                        :keepRatio false}
            :hideDefaultLines false
            :rotatable true}))

(def moveable-options-deselect
  (clj->js {:scalable false
            :resizable false
            :hideDefaultLines true
            :renderDirections []
            :rotatable false}))

(def moveable-options-drag
  (clj->js {:draggable true
            :scalable false
            :hideDefaultLines false
            :renderDirections []
            :rotatable false}))

(defn- target-element-for-uuid [uuid]
  (js/document.getElementById (str "target-" uuid)))

(defn- set-movable-state!
  "Atomically update the Moveable's underlying React state — switching modes
   (individual ↔ group) is much smoother through one setState than several
   sequential property setters that each trigger their own re-render."
  [m props]
  (.setState ^js m (clj->js props)))

(def ^:private group-render-directions
  ;; The 8 cardinal/diagonal scale handles plus rotation. In group mode the
  ;; top-level `renderDirections` prop drives the bounding box, since the
  ;; per-target `individualGroupableProps` no longer applies.
  ["n" "nw" "e" "ne" "w" "sw" "s" "se"])

(declare select-options-for)

(defn select-movable-layer-uuid
  "Selects exactly one clip in Moveable, hides handles for everything else.
   Also resets target/individualGroupable so it switches back from group
   mode if the previous selection was a multi-target group."
  [uuid]
  (when-let [m @moveable-instance]
    (set-movable-state! m
      {:individualGroupable true
       :target ".target"
       :renderDirections []
       :hideDefaultLines true
       :individualGroupableProps
       (fn [element]
         (if (.contains (.-classList element)
                        (str "target-" uuid))
           (select-options-for element)
           moveable-options-deselect))})
    (.updateRect m)))

(defn select-movable-layer-group
  "Switches Moveable to group mode with the given clip UUIDs as targets.
   Uses one shared bounding box with combined drag/scale/rotate handles
   instead of one set of handles per clip."
  [uuids]
  (when-let [m @moveable-instance]
    (let [targets (->> uuids
                       (map target-element-for-uuid)
                       (filter some?)
                       (vec))]
      (when (>= (count targets) 2)
        (set-movable-state! m
          {:individualGroupable false
           :target targets
           :draggable true
           :scalable true
           :keepRatio true
           :rotatable true
           ;; The bounding box and resize handles are driven by these two
           ;; top-level props in group mode (the per-target
           ;; `individualGroupableProps` are inert here).
           :hideDefaultLines false
           :renderDirections group-render-directions})
        (.updateRect m)))))

(defn clear-movable-selection!
  "Hides all Moveable handles. Called on full deselect."
  ([]
   (when-let [m @moveable-instance]
     (clear-movable-selection! m)))
  ([m]
   (set-movable-state! m
     {:individualGroupable true
      :target ".target"
      :renderDirections []
      :hideDefaultLines true
      :individualGroupableProps (fn [_element] moveable-options-deselect)})
   (.updateRect m)))
(defn text-element? [element]
  (= "text" (.getAttribute element "data-asset-type")))

(defn select-options-for [element]
  (if (text-element? element)
    moveable-options-select-text
    moveable-options-select))

(defn select-movable-layer [m e]
  (set! (.-individualGroupableProps ^js m)
        (fn [element]
          (let [uuid (parse-uuid (.getAttribute e.target "data-uuid"))]
            (if (.contains (.-classList element)
                           (str "target-" uuid))
              (select-options-for element)
              moveable-options-deselect)))))

(defn drag-movable-layer [m e targets]

  (set! (.-elementGuidelines ^js m)
        (let [current-selected-target (str ".target-" (parse-uuid (.getAttribute e.target "data-uuid")))
              re (filterv #(not= current-selected-target %) targets)]
          (clj->js re)))

  (set! (.-individualGroupableProps ^js m)
        (fn [element]
          (let [uuid (parse-uuid (.getAttribute e.target "data-uuid"))]
            (if (.contains (.-classList element)
                           (str "target-" uuid))
              moveable-options-drag
              moveable-options-deselect))))

  (.updateRect m))

(defn add-movable-container
  "Adds the movable-container element and all target elements for movablejs."
  [node clips]
  (let [html (rstring/render
               [:div
                {:id "movable-container"}
                (for [clip clips
                      ;; A clip can point to an asset stub without a
                      ;; type (e.g. an upload callback that re-created
                      ;; the asset after it was deleted); `name` on nil
                      ;; would throw and unmount the whole composition
                      ;; (STORRITO-V2-H6).
                      :let [asset (:clip/asset clip)]
                      :when (:asset/type asset)]
                  [:div
                   {:id (str "target-" (:clip/uuid clip))
                    :class ["target" (str "target-" (:clip/uuid clip))]
                    :data-uuid (str (:clip/uuid clip))
                    :data-asset-type (name (:asset/type asset))
                    :style (let [transform (some-> clip
                                                   (:clip/transform)
                                                   (transform-str))]
                             {:position "absolute"
                              :width (str (:asset/width asset) "px")
                              :height (str (:asset/height asset) "px")
                              :top "0px"
                              :left "0px"
                              :z-index 1
                              ;; These are shadow elements Moveable
                              ;; tracks for transform / bounding box
                              ;; calculations — the user never
                              ;; interacts with them directly
                              ;; (Moveable routes all gestures through
                              ;; its z-index 3000 control-box on
                              ;; `body`). Without `pointer-events:
                              ;; none`, the targets sit above the
                              ;; design clips and absorb mousedowns
                              ;; that fall outside the group bounding
                              ;; box, so design.cljs's
                              ;; `:on {:mousedown}` handler never
                              ;; fires and shift-click can't add the
                              ;; clip to a multi-selection.
                              :pointer-events "none"
                              :box-sizing "border-box"
                              :--transform transform
                              :transform transform
                              :transform-origin "top left"})}])])]
    (set! (.-innerHTML node) html)))

(def movable-options
  (let [design-width (:design/width defaults/defaults)
        design-height (:design/height defaults/defaults)]
    {:target ".target"
     :individualGroupable true
     :draggable true
     :scalable true
     :resizable false
     :keepRatio true
     :rotatable false
     :rotationPosition "center"
     :snappable true
     ;; Pixel distance at which the dragged edge snaps to a guideline.
     ;; Was 100 (≈10% of canvas width!) which made every drag feel like
     ;; it was being yanked toward the guidelines.
     :snapThreshold 15
     :snapHorizontalThreshold 15
     :snapVerticalThreshold 15
     :verticalGuidelines #js[0 100 (/ design-width 2) (- design-width 100) design-width]
     :horizontalGuidelines #js[0 180 (/ design-height 2) (- design-height 180) design-height]
     :snapDirections (clj->js {:left true
                               :top true
                               :right true
                               :bottom true
                               :center true
                               :middle true})
     ;; Max distance to look for guideline / gap candidates from neighbouring
     ;; clips. 1000px effectively meant "always" on a 1080px canvas; trim it
     ;; down so we only snap to neighbours that are actually close.
     :maxSnapElementGuidelineDistance 50
     :maxSnapElementGapDistance 50
     :renderDirections (clj->js [])
     :hideDefaultLines true
     :origin false
     :displayAroundControls true
     :isDisplaySnapDigit false
     ;; Note: previously misspelled "snapRotataionThreshold" (typo) which
     ;; Moveable silently ignored, falling back to the 5° default.
     :snapRotationThreshold 5
     ;; Multiples of 45° around the full circle. Previously had `305` here
     ;; which is a typo for `315` and left a 50° dead zone where rotations
     ;; near 315° wouldn't snap.
     :snapRotationDegrees (clj->js [0 45 90 135 180 225 270 315])
     }))

(defn- arr->vec3 [arr z]
  [(aget arr 0) (aget arr 1) z])

(defn- dispatch-translate! [dispatch! e ev]
  (let [uuid (parse-uuid (.getAttribute (.-target ev) "data-uuid"))
        translate (.-translate ev)]
    (dispatch!
      {:replicant/js-event e
       :actions [[:editor2/transform-clip
                  {:translate (arr->vec3 translate 0)}
                  uuid]]})))

(defn- dispatch-rotate! [dispatch! e ev]
  ;; Rotating around a non-pivot point also shifts translate (`e.drag` carries
  ;; the cumulative drag-adjusted translate). Persist both so the design
  ;; clip re-renders to the same place Moveable just put the phantom.
  (let [uuid (parse-uuid (.getAttribute (.-target ev) "data-uuid"))
        rotate (.-rotate ev)
        drag (.-drag ev)
        translate (some-> drag .-translate)]
    (dispatch!
      {:replicant/js-event e
       :actions [[:editor2/transform-clip
                  (cond-> {:rotate rotate}
                          translate (assoc :translate (arr->vec3 translate 0)))
                  uuid]]})))

(defn- dispatch-scale! [dispatch! e ev]
  ;; Same story as rotate: scaling from a side/corner handle shifts translate
  ;; to keep the opposite side fixed; without persisting that translate the
  ;; design clip drifts out from under the bounding box.
  (let [uuid (parse-uuid (.getAttribute (.-target ev) "data-uuid"))
        scale (.-scale ev)
        drag (.-drag ev)
        translate (some-> drag .-translate)]
    (dispatch!
      {:replicant/js-event e
       :actions [[:editor2/transform-clip
                  (cond-> {:scale (arr->vec3 scale 1)}
                          translate (assoc :translate (arr->vec3 translate 0)))
                  uuid]]})))

(defn- closest-clip-uuid
  [element]
  (when (and element (.-closest element))
    (some-> element
            (.closest "[data-uuid]")
            (.getAttribute "data-uuid")
            parse-uuid)))

(defn- moveable-event-clip-uuid
  [e]
  ;; With `dragArea: true`, Moveable's native input event starts on its
  ;; `.moveable-area` overlay. Moveable still computes the actual element under
  ;; the cursor and exposes it as `inputTarget`; use that before falling back to
  ;; the original DOM event target.
  (or (closest-clip-uuid (.-inputTarget e))
      (closest-clip-uuid (some-> (.-inputEvent e) (.-target)))
      (closest-clip-uuid (.-target e))))

(defn- moveable-event-double-click?
  [e]
  (or (.-isDouble e)
      (<= 2 (or (some-> (.-inputEvent e) (.-detail))
                0))))

(defn- dispatch-clip-click! [dispatch! e uuid]
  (if (moveable-event-double-click? e)
    (dispatch! {:replicant/js-event (or (.-inputEvent e) e)
                :actions [[:editor2/open-sticker-edit-modal uuid]]})
    (dispatch! {:replicant/js-event (or (.-inputEvent e) e)
                :actions [[:editor2/click-clip uuid]]})))

(defn register-movable-event-functions
  "Register all movable event functions like click, dragStart, dragEnd etc."
  [state m targets]
  (let [dispatch! (:replicant/dispatch! state)]
    (.on m
         "click"
         (fn [e]
           ;; `e.target` on a Moveable click event is the currently *active*
           ;; target (whatever has handles), not necessarily the element under
           ;; the click. Resolve the clip from Moveable's `inputTarget`, which
           ;; accounts for the drag-area overlay used by selected clips.
           (when-let [uuid (moveable-event-clip-uuid e)]
             (dispatch-clip-click! dispatch! e uuid))))

    (.on m
         "dragStart"
         (fn [e]
           (drag-movable-layer m e targets)))
    (.on m
         "dragEnd"
         (fn [e]
           (let [uuid (parse-uuid (.getAttribute e.target "data-uuid"))]
             (if (.-isDrag e)
               (dispatch! {:replicant/js-event e
                           :actions [[:editor2/select-clip uuid]]})
               ;; Moveable fires dragStart/dragEnd for plain clicks too. The
               ;; dragStart handler temporarily switches the target to drag-only
               ;; options; restore the normal selected handles here without
               ;; touching application selection state, so modifier-click and
               ;; double-click handlers can still make their own decision.
               (select-movable-layer-uuid uuid)))))
    ;; Per-event state updates use the cumulative values Moveable carries on
    ;; each event (`e.translate`, `e.rotate`, `e.scale`). Those are written
    ;; by Moveable's own snap-aware code path. Going through `transformObject`
    ;; in the render event meant the persisted value could disagree with
    ;; what Moveable just snapped to — visible as the asset jumping to a
    ;; different snap angle and then "fixing itself" on the next gesture.
    (.on m
         "drag"
         (fn [e]
           (set! e.target.style.transform e.transform)
           (dispatch-translate! dispatch! e e)))
    (.on m
         "scale"
         (fn [e]
           ;; Clamp the gesture to the clip's minimal scale (music
           ;; stickers mirror the Instagram app's size limits; every
           ;; clip stops before zero/mirroring). Below the limit the
           ;; phantom keeps its last valid transform and the dispatched
           ;; transform is clamped by `actions/clamp-min-scale`:
           (let [uuid (parse-uuid (.getAttribute (.-target e) "data-uuid"))
                 clip (ds/entity (get-in state [:editor2 :db])
                                 [:clip/uuid uuid])
                 [sx] (arr->vec3 (.-scale e) 1)]
             (when (>= sx (common/min-clip-scale clip))
               (set! e.target.style.transform e.drag.transform))
             (dispatch-scale! dispatch! e e))))
    ;; When a scale gesture ended below the minimal scale, the target
    ;; kept its last valid transform while Moveable's internal state
    ;; kept shrinking — re-read the target rects so the control box
    ;; snaps back onto the clip:
    (.on m
         "scaleEnd"
         (fn [_e]
           (.updateRect m)))
    (.on m
         "scaleGroupEnd"
         (fn [_e]
           (.updateRect m)))
    (.on m
         "rotate"
         (fn [e]
           (set! e.target.style.transform e.drag.transform)
           (dispatch-rotate! dispatch! e e)))
    (.on m
         "resize"
         (fn [e]
           (set! e.target.style.width (str (.-width e) "px"))
           (set! e.target.style.transform (.-transform (.-drag e)))
           ;; Update design panel element directly for real-time visual
           ;; feedback. Scope the lookup to `#composition-design` so we hit
           ;; the design clip and not the Moveable target div (which also
           ;; carries `data-uuid`).
           (let [uuid (.getAttribute e.target "data-uuid")]
             (when-let [design-el (.querySelector js/document
                                                  (str "#composition-design [data-uuid=\"" uuid "\"]"))]
               (set! (.. design-el -style -width) (str (.-width e) "px"))))))
    (.on m
         "resizeEnd"
         (fn [e]
           (when-let [last-event (.-lastEvent e)]
             (let [uuid (parse-uuid (.getAttribute e.target "data-uuid"))
                   width (.-width last-event)]
               (dispatch! {:replicant/js-event e
                           :actions [[:editor2/resize-text-clip! uuid width]]})))))
    (.on m
         "rotateStart"
         (fn [e]
           ;; Takes care that MovableJS still rotates around the middel
           ;; while the transform-origin is top-left. Thereby we mimic
           ;; the behaviour of FabricJS (our old editor) and Canva.com:
           (.setFixedDirection e
                               #js [0, 0])
           ;; Found here:
           ;; https://daybrush.com/moveable/storybook/?path=/story/advanced-rotatable--advanced-rotatable-custom-origin
           ))

    ;; Group events (fire when Moveable's `target` is an array of elements).
    ;; Each per-child event in `e.events` carries the same cumulative
    ;; properties (`translate`, `rotate`, `scale`) as the single-target
    ;; events above.
    (.on m
         "dragGroup"
         (fn [e]
           (doseq [ev (.-events e)]
             (set! (.. ev -target -style -transform) (.-transform ev))
             (dispatch-translate! dispatch! e ev))))
    (.on m
         "scaleGroup"
         (fn [e]
           (doseq [ev (.-events e)]
             ;; Same minimal-scale clamp as the single-target scale
             ;; event above:
             (let [uuid (parse-uuid (.getAttribute (.-target ev) "data-uuid"))
                   clip (ds/entity (get-in state [:editor2 :db])
                                   [:clip/uuid uuid])
                   [sx] (arr->vec3 (.-scale ev) 1)]
               (when (>= sx (common/min-clip-scale clip))
                 (set! (.. ev -target -style -transform) (.. ev -drag -transform)))
               (dispatch-scale! dispatch! e ev)))))
    (.on m
         "rotateGroup"
         (fn [e]
           (doseq [ev (.-events e)]
             (set! (.. ev -target -style -transform) (.. ev -drag -transform))
             (dispatch-rotate! dispatch! e ev))))

    ;; Clicking inside the group bounding box: route to the specific clip
    ;; the click landed on so the user can shift-click to remove it from the
    ;; selection or click without modifier to narrow to that single clip.
    (.on m
         "clickGroup"
         (fn [e]
           ;; `inputTarget` is whatever sits directly under the cursor
           ;; (Moveable's areaElement gets `pointer-events: none` during
           ;; the gesture, so the topmost element is usually a child of
           ;; the clip — its `<img>`/`<video>`, etc. — not the
           ;; `[data-uuid]` div itself). Walk up to find the clip wrapper
           ;; or the shift-click-to-remove path silently no-ops.
           (when-let [uuid (moveable-event-clip-uuid e)]
             (dispatch-clip-click! dispatch! e uuid))))))

(defn movable
  "DIV for movablejs"
  [state]
  (let [db (get-in state
                   [:editor2
                    :db])
        clips (common/get-visible-clips state)]
    [:div
     {:replicant/key (hash
                       (conj (mapv
                               (fn [e]
                                 (let [asset (ds/entity db [:clip/uuid (:clip/uuid e)])
                                       asset-width (get-in asset [:clip/asset :asset/width])
                                       asset-height (get-in asset [:clip/asset :asset/height])]
                                   [(:clip/uuid e) asset-width asset-height]))
                               clips)
                             (get-composition-zoom state)))
      :replicant/on-unmount (fn [{:keys [replicant/memory]}]
                              (.destroy memory))
      :replicant/on-mount (fn [{:keys [replicant/node replicant/remember]}]
                            (prn "MOUNT")
                            (add-movable-container node clips)
                            (let [container (js/document.getElementById "movable-container")
                                  targets (into [] (map (fn [clip]
                                                          (str ".target-" (:clip/uuid clip)))
                                                        clips))
                                  m (Moveable.
                                      container
                                      (clj->js (-> movable-options
                                                   (assoc :zoom (get-composition-zoom state)
                                                          :elementGuidelines targets))))]
                              (remember m)
                              (reset! moveable-instance m)
                              (register-movable-event-functions state m targets)
                              ;; Re-apply the current selection (Moveable was
                              ;; just re-created on a clip-set or zoom change
                              ;; and lost any previous selection markings).
                              (let [selected (common/selected-clip-uuids state)]
                                (cond
                                  (>= (count selected) 2)
                                  (select-movable-layer-group selected)
                                  (= 1 (count selected))
                                  (select-movable-layer-uuid (first selected))))))
      }]))

(defn composition
  [state]
  [:div.relative.w-full.h-full#editor-composition
   [:style
    ;; Suppress native text selection across the whole canvas. The
    ;; marquee, click-clip, and Moveable drags all start with a
    ;; `mousedown` that the browser otherwise interprets as the start of
    ;; a text selection — so dragging across a text clip leaves the clip
    ;; (and sometimes the surrounding `#composition-design` chrome)
    ;; visually highlighted. `contenteditable` regions (ProseMirror)
    ;; still ignore this rule per the CSS spec, so in-place text editing
    ;; keeps working.
    "#editor-composition,
     #editor-composition * {
       -webkit-user-select: none;
       -moz-user-select: none;
       -ms-user-select: none;
       user-select: none;
     }
     #editor-composition [contenteditable=\"true\"],
     #editor-composition [contenteditable=\"true\"] * {
       -webkit-user-select: text;
       -moz-user-select: text;
       -ms-user-select: text;
       user-select: text;
     }
     .moveable-control[data-direction=\"e\"],
     .moveable-control[data-direction=\"w\"] {
       width: 8px !important;
       height: 32px !important;
       border-radius: 4px !important;
       margin-top: -16px !important;
     }"]
   [:div
    {:class ["absolute" "top-1" "right-4" "bg-gray-800" "border" "border-slate-600" "z-50"]}
    [:button
     {:class ["hover:bg-gray-900" "p-1" "rounded-md"]
      :on {:click [[:editor2/composition-zoom-out]]}}
     common/zoom-out-icon]
    [:button
     {:class ["hover:bg-gray-900" "p-1" "rounded-md" "ml-1"]
      :on {:click [[:editor2/composition-zoom-in]]}}
     common/zoom-in-icon]]
   [:div#editor-container
    {:style {:display "flex"
             :position "relative"
             :align-items "center"
             :justify-content "center"
             :overflow "auto"                               ;; Allows to scroll the composition area when you
             ;; zoom in a lot.
             :width "100%"
             :height "100%"}
     :replicant/on-mount
     (fn [{:keys [replicant/node replicant/remember]}]
       (let [dispatch! (:replicant/dispatch! state)
             ;; Shift-click extends the document's existing selection
             ;; range from its anchor — even if both anchor and focus
             ;; are inside `user-select: none` regions, the browser will
             ;; still paint the cross-element range when the anchor was
             ;; placed outside the canvas (e.g. an earlier text-select
             ;; in the sidebar). Wipe the range as soon as a shift-
             ;; mousedown lands inside the editor so the highlight can
             ;; never form. Capture-phase so it runs before Moveable /
             ;; Selecto handlers that might rely on the event.
             clear-selection-on-shift
             (fn [e]
               (when (.-shiftKey e)
                 (some-> js/window .getSelection .removeAllRanges)))
             _ (.addEventListener node "mousedown"
                                  clear-selection-on-shift
                                  true)
             s (Selecto.
                 #js {:container js/document.body
                      :dragContainer node
                      :selectableTargets #js ["#composition-design [data-uuid]"]
                      ;; 100% overlap = fully contained, Canva-style.
                      :hitRate 100
                      ;; Clicks still flow through Moveable's own click
                      ;; handler (with the click-clip debounce) — Selecto only
                      ;; handles drag-rectangles.
                      :selectByClick false
                      ;; Mousedown that lands on a clip starts a Moveable
                      ;; drag, not a marquee.
                      :selectFromInside false
                      ;; The default `true` makes Selecto call
                      ;; `e.stopPropagation()`/`preventDefault()` on mousedown
                      ;; that lands on a selectable target, which would block
                      ;; Moveable from ever receiving the click. Let the event
                      ;; through — Moveable handles the click-to-select.
                      :preventDragFromInside false
                      :continueSelect false
                      :ratio 0
                      :preventDefault true
                      ;; Only suppress the trailing click after an actual
                      ;; drag (the marquee). `preventClickEventOnDragStart`
                      ;; would also eat plain clicks on empty canvas, which
                      ;; would block the editor-container's click handler
                      ;; that drives `:editor2/deselect-clips`.
                      :preventClickEventOnDrag true
                      ;; Only initiate a marquee on truly empty canvas. Bail
                      ;; whenever the mousedown lands on:
                      ;;  - a Moveable handle / drag area (`.moveable-control-box`)
                      ;;  - any clip element (`[data-uuid]`) — Moveable's own
                      ;;    target divs live in `#movable-container` (not in
                      ;;    `#composition-design`), so `selectableTargets`
                      ;;    alone doesn't catch them. Without this guard a
                      ;;    drag started on an unselected clip would draw a
                      ;;    marquee instead of letting Moveable move the clip.
                      :dragCondition
                      (fn [e]
                        (let [t (some-> (.-inputEvent e) (.-target))]
                          (and t
                               (not (.closest t ".moveable-control-box"))
                               (not (.closest t "[data-uuid]")))))})]
         (.on s "selectEnd"
              (fn [e]
                ;; Selecto fires `selectEnd` for every gesture it tracked,
                ;; including no-movement clicks. Only commit a selection on
                ;; an actual marquee drag — otherwise a click on a clip
                ;; (handled by `click-clip`) would be immediately overwritten
                ;; by an empty-result `select-multiple-clips`. The `selectEnd`
                ;; event payload only carries `isClick`, so we gate on that.
                (when-not (.-isClick e)
                  (let [uuids (->> (.-selected e)
                                   array-seq
                                   (keep #(some-> %
                                                  (.getAttribute "data-uuid")
                                                  parse-uuid))
                                   set)
                        ;; Gesto ends a drag on window blur (iOS app switch,
                        ;; keyboard, tab change) by calling `onDragEnd` without
                        ;; a native event, so `inputEvent` can be undefined.
                        input (.-inputEvent e)
                        extend? (boolean (and input
                                              (or (.-shiftKey input)
                                                  (.-metaKey input)
                                                  (.-ctrlKey input))))]
                    (dispatch!
                      {:replicant/js-event (or input e)
                       :actions [[:editor2/select-multiple-clips uuids extend?]]})))))
         (remember #js {:selecto s
                        :clear-selection-on-shift clear-selection-on-shift
                        :node node})
         (reset! selecto-instance s)))
     :replicant/on-unmount
     (fn [{:keys [replicant/memory]}]
       (when-let [m ^js memory]
         (.destroy ^js (.-selecto m))
         (.removeEventListener (.-node m)
                               "mousedown"
                               (.-clear-selection-on-shift m)
                               true))
       (reset! selecto-instance nil))
     :on {:click [[:editor2/deselect-clips moveable-instance]]}}

    (overlay-mask
      state
      ;; Outer container that defines the visible editor viewport
      [:div {:id editor-container-id
             :tabindex 0
             :style {:grid-column 2
                     :grid-row 1
                     :position "relative"
                     :overflow "visible"
                     :width (str (/ (:design/width defaults/defaults)
                                    (get-composition-zoom state))
                                 "px")
                     :height (str (/ (:design/height defaults/defaults)
                                     (get-composition-zoom state))
                                  "px")
                     :background-color "black"}}
       [:div
        {:class ["absolute" "border" "border-grey-300" "w-full" "h-full" "z-10" "pointer-events-none"]}]

       ;; This wrapper is scaled when you zoom in/out
       [:div {:style {:transform (str "scale("
                                      (/ 1 (get-composition-zoom state))
                                      ")")
                      :transform-origin "top left"}}
        ;; The design container holds your movable elements
        (design/panel state)
        (movable state)
        ]])]])

(defmethod extension-points/composition-asset
  :default
  [_params]
  nil)

(defmethod extension-points/composition-asset
  :image
  [params]
  (let [asset (get-in params [:clip
                              :clip/asset])]
    [:img
     (merge
       {:src (:asset/uri asset)
        :draggable "false"}
       ;; Legacy quick story blurry background:
       ;; Disable for now until we got a solution how to render it.
       (when (some (fn [filter]
                     (= (:type filter) "Blur"))
                   (get-in asset
                           [:asset/data :legacy-filters]))
         {:style {:filter "blur(100px)"}}))]))

(defmethod extension-points/composition-asset
  :gif
  [params]
  [:img
   {:src (get-in params
                 [:clip
                  :clip/asset
                  :asset/uri])
    :draggable "false"}])

(defmethod extension-points/composition-asset
  :video
  [params]
  (let [clip (:clip params)]
    [:video
     (merge
       {:id (str "video-"
                 (:clip/uuid clip))
        :preload "auto"
        :draggable "false"
        ;; Necessary to prevent Opaque Response
        ;; Blocking (https://github.com/annevk/orb)
        :crossorigin "anonymous"
        :src (get-in clip
                     [:clip/asset
                      :asset/uri])}
       (when (:clip/muted clip)
         {:muted true}))]))

(defmethod extension-points/composition-asset
  :text
  [params]
  (let [asset-data (get-in params [:clip :clip/asset :asset/data])
        hiccup (:hiccup asset-data)
        tcw (:text-container-width asset-data)]
    (cond-> hiccup
      tcw (common/constrain-hiccup-width tcw))))
